import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  CheckCircle, Loader2, XCircle, Baby, ArrowRight
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { toast } from 'sonner';

import API from '@/config/api';
// API imported from config;

const SubscriptionSuccessPage = () => {
  const { getAuthHeaders, refreshUser, token } = useAuth();
  const { refreshSubscription } = useSubscription();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [subscription, setSubscription] = useState(null);
  const pollCountRef = useRef(0);
  const maxPollAttempts = 10;
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Auto-redirect to dashboard after payment success
  useEffect(() => {
    if (status !== 'success') return;
    const timer = setTimeout(async () => {
      if (refreshSubscription) await refreshSubscription();
      if (refreshUser) await refreshUser();
      navigate('/admin');
    }, 3000);
    return () => clearTimeout(timer);
  }, [status, navigate, refreshSubscription, refreshUser]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      // Use a timeout to defer state update
      const timeoutId = setTimeout(() => setStatus('error'), 0);
      return () => clearTimeout(timeoutId);
    }

    let cancelled = false;
    
    const pollPaymentStatus = async () => {
      if (cancelled || !isMountedRef.current) return;
      
      if (pollCountRef.current >= maxPollAttempts) {
        setStatus('error');
        toast.error('Payment verification timed out. Please contact support.');
        return;
      }

      console.log(`[SubscriptionSuccess] Polling payment status, attempt ${pollCountRef.current + 1}/${maxPollAttempts}`);

      try {
        const res = await axios.get(
          `${API}/subscriptions/checkout/status/${sessionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (cancelled || !isMountedRef.current) return;

        console.log(`[SubscriptionSuccess] API response:`, res.data);

        // Handle both 'paid' and 'no_payment_required' (for 100% off coupons)
        if (res.data.payment_status === 'paid' || res.data.payment_status === 'no_payment_required') {
          console.log(`[SubscriptionSuccess] Payment successful! Setting status to success`);
          setStatus('success');
          setSubscription(res.data.subscription);
          // Refresh both user data AND subscription context
          if (refreshUser) {
            console.log(`[SubscriptionSuccess] Refreshing user...`);
            await refreshUser();
          }
          if (refreshSubscription) {
            console.log(`[SubscriptionSuccess] Refreshing subscription context...`);
            await refreshSubscription();
          }
          console.log(`[SubscriptionSuccess] All refreshes complete`);
          toast.success('Subscription activated successfully!');
        } else if (res.data.status === 'expired') {
          console.log(`[SubscriptionSuccess] Session expired`);
          setStatus('error');
          toast.error('Payment session expired.');
        } else {
          // Continue polling
          console.log(`[SubscriptionSuccess] Payment not complete yet, continuing to poll...`);
          pollCountRef.current += 1;
          setTimeout(pollPaymentStatus, 2000);
        }
      } catch (error) {
        if (cancelled || !isMountedRef.current) return;
        
        console.error('[SubscriptionSuccess] Error checking payment status:', error);
        console.error('[SubscriptionSuccess] Error response:', error.response?.data);
        console.error('[SubscriptionSuccess] Error status:', error.response?.status);
        
        // If unauthorized, don't keep polling - show error
        if (error.response?.status === 401 || error.response?.status === 403) {
          console.error('[SubscriptionSuccess] Authentication error - stopping poll');
          setStatus('error');
          toast.error('Session expired. Please log in again.');
          return;
        }
        
        // For other errors, continue polling (could be temporary)
        pollCountRef.current += 1;
        setTimeout(pollPaymentStatus, 2000);
      }
    };

    pollPaymentStatus();
    
    return () => { cancelled = true; };
  }, [searchParams, token, refreshUser, refreshSubscription]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 text-primary mx-auto mb-4 animate-spin" />
              <h1 className="font-heading font-bold text-2xl mb-2">
                Processing Payment...
              </h1>
              <p className="text-muted-foreground">
                Please wait while we confirm your subscription.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h1 className="font-heading font-bold text-2xl mb-2">
                Welcome to {subscription?.plan_name || 'CareKids WI'}!
              </h1>
              <p className="text-muted-foreground mb-6">
                Your subscription is now active. Redirecting to your dashboard...
              </p>
              
              {subscription && (
                <div className="bg-muted/50 rounded-lg p-4 mb-6 text-left">
                  <h3 className="font-semibold mb-2">Subscription Details</h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Plan</span>
                      <span className="font-medium">{subscription.plan_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Amount</span>
                      <span className="font-medium">${subscription.amount}/month</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Next billing</span>
                      <span className="font-medium">
                        {new Date(subscription.current_period_end).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Taking you to your dashboard...</span>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-12 h-12 text-red-600" />
              </div>
              <h1 className="font-heading font-bold text-2xl mb-2">
                Something Went Wrong
              </h1>
              <p className="text-muted-foreground mb-6">
                We couldn&apos;t verify your payment. If you were charged, please contact support.
              </p>
              
              <div className="space-y-3">
                <Button 
                  className="w-full" 
                  onClick={() => navigate('/pricing')}
                >
                  Try Again
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate('/dashboard')}
                >
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}

          <div className="mt-6 pt-6 border-t">
            <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <Baby className="w-5 h-5" />
              <span className="font-heading font-semibold">CareKids WI</span>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccessPage;
